/**
 * Mix & Match Discount Progress Bar
 * Tracks eligible Permanent Bangles, Cuffs & Cuff Rings in cart
 * and shows progress toward quantity-based discount tiers.
 */
class MixMatchProgress extends HTMLElement {
  constructor() {
    super();
    this.eligibleTitles = [
      'Permanent Bangle',
      'Domed Permanent Cuff',
      'Round Wire Permanent Cuff',
      'Half Round Wire Permanent Half Cuff',
      'Domed Permanent Half Cuff',
      'Round Permanent Half Cuff',
      'Round Permanent Bangle',
      'Round Wire Permanent Cuff Rings',
      'Domed Permanent Cuff Rings',
    ];
    this.tiers = [
      { qty: 6, discount: 5 },
      { qty: 12, discount: 15 },
      { qty: 18, discount: 25 },
    ];
  }

  connectedCallback() {
    this.barFill = this.querySelector('[data-mm-bar-fill]');
    this.messageEl = this.querySelector('[data-mm-message]');
    this.tierEls = this.querySelectorAll('[data-mm-tier]');
    this.wrapper = this.querySelector('[data-mm-wrapper]');

    this.updateFromCart();
    this.bindEvents();
  }

  bindEvents() {
    if (window.FoxThemeEvents && typeof PUB_SUB_EVENTS !== 'undefined') {
      window.FoxThemeEvents.subscribe(PUB_SUB_EVENTS.cartUpdate, () =>
        this.fetchAndUpdate(),
      );
    }
    document.addEventListener('cart:updated', () => this.fetchAndUpdate());
  }

  fetchAndUpdate() {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then((res) => res.json())
      .then((cart) => {
        const count = this.countEligibleItems(cart.items);
        this.render(count);
      })
      .catch(() => {});
  }

  updateFromCart() {
    const initialCount = parseInt(this.dataset.eligibleCount || '0', 10);
    this.render(initialCount);
  }

  countEligibleItems(items) {
    let count = 0;
    items.forEach((item) => {
      const title = (item.product_title || item.title || '').trim();
      if (this.isEligible(title)) {
        count += item.quantity;
      }
    });
    return count;
  }

  isEligible(title) {
    const normalizedTitle = title.toLowerCase();
    return this.eligibleTitles.some(
      (eligible) => normalizedTitle === eligible.toLowerCase(),
    );
  }

  render(count) {
    // Show or hide the entire component based on eligible count
    if (count > 0) {
      this.removeAttribute('hidden');
    } else {
      this.setAttribute('hidden', '');
      return;
    }

    const currentTier = this.getCurrentTier(count);
    const nextTier = this.getNextTier(count);
    const maxTier = this.tiers[this.tiers.length - 1];
    const progress = Math.min((count / maxTier.qty) * 100, 100);

    if (this.barFill) {
      this.barFill.style.setProperty('--mm-progress', `${progress}%`);
    }

    if (this.messageEl) {
      this.messageEl.innerHTML = this.getMessage(count, currentTier, nextTier);
    }

    this.tierEls.forEach((el) => {
      const tierQty = parseInt(el.dataset.mmTier, 10);
      el.classList.remove(
        'mm-progress__tier--active',
        'mm-progress__tier--reached',
      );
      if (count >= tierQty) {
        el.classList.add('mm-progress__tier--reached');
      } else if (nextTier && tierQty === nextTier.qty) {
        el.classList.add('mm-progress__tier--active');
      }
    });

    if (this.wrapper) {
      this.wrapper.classList.toggle(
        'mm-progress--success',
        count >= maxTier.qty,
      );
      if (currentTier && !this.wrapper.dataset.celebratedTier) {
        this.wrapper.dataset.celebratedTier = currentTier.qty;
        this.celebrate();
      } else if (
        currentTier &&
        parseInt(this.wrapper.dataset.celebratedTier) < currentTier.qty
      ) {
        this.wrapper.dataset.celebratedTier = currentTier.qty;
        this.celebrate();
      }
    }
  }

  getCurrentTier(count) {
    let current = null;
    for (const tier of this.tiers) {
      if (count >= tier.qty) current = tier;
    }
    return current;
  }

  getNextTier(count) {
    for (const tier of this.tiers) {
      if (count < tier.qty) return tier;
    }
    return null;
  }

  getMessage(count, currentTier, nextTier) {
    if (count === 0) {
      return `<strong>Mix & Match</strong> Permanent Bangles, Cuffs & Cuff Rings — add items to unlock up to <strong>25% off!</strong>`;
    }
    if (!nextTier) {
      return `🎉 <strong>Congrats!</strong> You've unlocked <strong>${currentTier.discount}% off</strong> your ${count} eligible items!`;
    }
    const remaining = nextTier.qty - count;
    if (currentTier) {
      return `✨ <strong>${currentTier.discount}% off</strong> unlocked! Add <strong>${remaining} more</strong> item${remaining !== 1 ? 's' : ''} to get <strong>${nextTier.discount}% off!</strong>`;
    }
    return `You're <strong>${remaining} item${remaining !== 1 ? 's' : ''} away</strong> from <strong>${nextTier.discount}% off</strong> — keep adding!`;
  }

  celebrate() {
    if (this.wrapper) {
      this.wrapper.classList.add('mm-progress--celebrating');
      setTimeout(() => {
        this.wrapper.classList.remove('mm-progress--celebrating');
      }, 2000);
    }
  }
}

if (!customElements.get('mix-match-progress')) {
  customElements.define('mix-match-progress', MixMatchProgress);
}
